import * as Hangul from 'hangul-js'
import { sortBy } from 'lodash-es'
import { makeAutoObservable } from 'mobx'

import type { Iter, ObjKey } from '@/types'

import { getValueFromKeyIter, typeSafeAssign } from '../common'

class ListBox<K extends keyof V, V extends { [key in ObjKey]: any }> {
  private map: Map<V[K], V>
  private keyIter: Iter<K, V>

  constructor(iterableIterator: IterableIterator<V>, keyIter: Iter<K, V>) {
    makeAutoObservable(this)

    this.keyIter = keyIter

    this.map = new Map([...iterableIterator].map((val) => [getValueFromKeyIter(val, keyIter), val]))
  }

  get entries() {
    return this.map.entries
  }

  get toArr() {
    return [...this.values()]
  }

  get size() {
    return this.map.size
  }

  get isEmpty() {
    return this.size === 0
  }

  get isNotEmpty() {
    return this.size !== 0
  }

  toSortArrByKey<keyT extends keyof V>(k: keyT) {
    return sortBy([...this.values()], (v) => v[k])
  }

  search<keyT extends keyof V>(field: keyT, searchValue: string) {
    const searcher = new Hangul.Searcher(searchValue)
    return [...this.map.values()].filter((value) => searcher.search(value[field]) !== -1)
  }

  searchResultMap<keyT extends keyof V>(field: keyT, searchValue: string) {
    if (searchValue === '') return this.map

    const searcher = new Hangul.Searcher(searchValue)
    const map = new Map<V[K], V>()

    for (const [key, value] of this.map) {
      const searchValue = value[field]
      if (searcher.search(searchValue) !== -1) {
        map.set(key, value)
      }
    }

    return map
  }

  matchKeys(keys: Array<V[K]>) {
    return keys.filter((v) => this.has(v))
  }

  matchSearchKeys<keyT extends keyof V>(keys: Array<V[K]>, field: keyT, searchValue: string) {
    if (searchValue === '') return this.matchKeys(keys)

    const map = this.searchResultMap(field, searchValue)
    return keys.filter((key) => map.has(key))
  }

  delete(key: V[K]) {
    this.map.delete(key)
  }

  deletes(keys: Array<V[K]>) {
    keys.forEach(this.map.delete)
  }

  add(keyField: V[K], list: V[]) {
    list.forEach((v) => this.map.set(keyField, v))
  }

  has(key: V[K]) {
    return this.map.has(key)
  }

  update(key: V[K], data: Partial<V>) {
    return typeSafeAssign(this.map.get(key), data)
  }

  get(key: V[K]) {
    return this.map.get(key)
  }

  values() {
    return this.map.values()
  }

  keys() {
    return this.map.keys()
  }
}

type ConstructorParam<K extends keyof V, V extends { [key in ObjKey]: any }> = {
  leftIter?: IterableIterator<V>
  rightIter?: IterableIterator<V>
  keyIter: K | ((value: V) => V[K])
}

class DualListBox<K extends keyof V, V extends { [key in ObjKey]: any }> {
  leftList: ListBox<K, V>
  rightList: ListBox<K, V>
  private keyIter: ConstructorParam<K, V>['keyIter']

  constructor({
    leftIter = [].values(),
    rightIter = [].values(),
    keyIter,
  }: ConstructorParam<K, V>) {
    makeAutoObservable(this)

    this.leftList = new ListBox<K, V>(leftIter, keyIter)
    this.rightList = new ListBox<K, V>(rightIter, keyIter)
    this.keyIter = keyIter
  }

  moveLeftToRight(keys: V[K][]) {
    this.moveAtoB(this.leftList, this.rightList, keys)
  }

  moveRightToLeft(keys: V[K][]) {
    this.moveAtoB(this.rightList, this.leftList, keys)
  }

  moveRightToLeftAll() {
    this.moveAtoB(this.rightList, this.leftList, [...this.rightList.keys()])
  }

  moveLeftToRightAll() {
    this.moveAtoB(this.leftList, this.rightList, [...this.leftList.keys()])
  }

  leftMatchKeys(keys: Array<V[K]>) {
    return this.leftList.matchKeys(keys)
  }

  rightMatchKeys(keys: Array<V[K]>) {
    return this.rightList.matchKeys(keys)
  }

  private moveAtoB(a: ListBox<K, V>, b: ListBox<K, V>, keys: V[K][]) {
    keys.forEach((key) => {
      const has = a.has(key)
      if (has) {
        const value = a.get(key)!

        a.delete(key)
        b.add(key, [value])
      }
    })
  }
}

export default DualListBox
